// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import apiFetch from '@wordpress/api-fetch';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
import { confirm } from '@automator/shared/utils/confirm';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Add the nonce as middleware. This automatically adds the `X-WP-Nonce` header to requests.
apiFetch.use( apiFetch.createNonceMiddleware( UncannyAutomatorBackend.rest.nonce ) );

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-template-button-import-recipe' )
export class AutomatorTemplateButtonImportRecipe extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The ID of the template to be imported.
	 *
	 * @type {Number}
	 * @property {Number} templateID - Represents the unique ID of the template.
	 * Attribute name in HTML: `template-id`.
	 */
	@property( { type: Number, attribute: 'template-id' } )
	accessor templateID;

	/**
	 * Indicates the loading state of the import button.
	 * When `true`, the button is disabled, and a loading animation is shown.
	 *
	 * @type {Boolean}
	 * @property {Boolean} loading - Specifies if the button is in a loading state.
	 * Attribute name in HTML: none (non-reflected property).
	 * Default: `false`.
	 */
	@property( { type: Boolean } )
	accessor loading = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		// Get data or show placeholder
		return html`
			<div class="import-button">
				<uo-button
					@click=${ this._handleImportRecipe }
					?loading=${ this.loading }
					?disabled=${ this.loading }
				>
					${ escapeHTML( __( 'Import recipe to your site', 'uncanny-automator' ) ) }
				</uo-button>
			</div>
		`;
	}

	/**
	 * Handles the import recipe button click
	 */
	_handleImportRecipe() {
		// Check if it's loading, then abort
		if ( this.loading ) {
			return;
		}

		// Do the request
		this.doRequest();
	}

	/**
	 * Do the request to import the recipe
	 */
	doRequest() {
		// As soon as the call starts, set the loading animation
		this.loading = true;

		apiFetch( {
			url: `${ UncannyAutomatorBackend.rest.url }/template-library`,
			method: 'POST',
			data: {
				event: 'template-library-import',
				template: this.templateID
			}
		} )
			.then( response => {
				// Check if it was successful
				if ( response.success && ! isEmpty( response.redirect ) ) {
					// Redirect to the new recipe
					window.location.href = response.redirect;

					// Stop here
					return;
				}

				// Handle error case
				if ( ! response.success ) {
					const errorMessage = ! isEmpty( response.error )
						? response.error
						: escapeHTML( __( 'Unknown error', 'uncanny-automator' ) );
					throw new Error( errorMessage );
				}

				// Stop loading animation
				this.loading = false;
			} )
			.catch( async error => {
				// Stop loading animation
				this.loading = false;

				// Get the error message
				const fatalErrorMessage = ! isEmpty( error.message )
					? error.message
					: escapeHTML( __( 'Unknown error', 'uncanny-automator' ) );

				// Show the error, and offer the user the option to retry
				await confirm( {
					heading: escapeHTML( __( 'Something went wrong', 'uncanny-automator' ) ),
					content: html`
						<uo-alert type="error">
							${ fatalErrorMessage }
						</uo-alert>
					`,
					confirmButtonLabel: escapeHTML( __( 'Try again', 'uncanny-automator' ) ),
					cancelButtonLabel: escapeHTML( __( 'Close', 'uncanny-automator' ) )
				} );

				// Try again
				this.doRequest();
			} );
	}
}
